/**
 * features/finance/PaymentBatches.tsx
 * ------------------------------------------------------------------
 * ONE FILE, NINETY SUPPLIERS, AND EVERY WAY THAT GOES WRONG.
 *
 * Until now a voucher was paid by somebody typing it into the bank, one
 * at a time. That does not scale past a handful, and the workaround is
 * a spreadsheet nobody checks.
 *
 * THE VALUE IS IN WHAT HAPPENS BEFORE THE FILE IS WRITTEN. A Botswana
 * bulk-payment file is all-or-nothing: one bad character in one
 * beneficiary name rejects the whole thing, and the rejection arrives
 * the next day, by which time the run is "sent" and nobody knows which
 * of ninety rows broke it. So every blocker is surfaced here, on screen,
 * against the payee's name — missing account number, wrong length for
 * the format, no branch code, a reference that will be truncated.
 *
 * AND THE DUPLICATE GUARD. Two payments to one payee for the same amount
 * inside a week is far more often an invoice paid twice than a
 * coincidence. It warns rather than blocks, because identical weekly
 * charges are real and a block people work around is worse than none.
 *
 * PAID MEANS THE BANK CONFIRMED IT. Exporting the file and confirming
 * the payment are two acts. A run marked paid on export puts money in
 * the books on a day it may not have left.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import {
  Landmark, Download, AlertTriangle, CheckCircle2, Ban, ArrowLeft, Copy,
  ShieldAlert, Send,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import {
  BATCH_COLLECTION, BATCH_STATUS_LABEL, batchableVouchers, batchTotal,
  batchToBankRows, duplicateWarnings,
  type PaymentBatch, type BatchStatus,
} from '@/lib/paymentBatches';
import {
  BANK_FORMATS, bankFormatById, buildBankFile, downloadBankFile,
  validateBankRows, hasBlockers,
} from '@/lib/bankFile';
import { VOUCHER_COLLECTIONS, type Voucher } from '@/lib/vouchers';
import { SUPPLIER_COLLECTION, type Supplier } from '@/lib/suppliers';
import {
  createBatch, markBatchExported, confirmBatch, cancelBatch,
  type ConfirmResult,
} from '@/features/finance/batchWrite';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_TONE: Record<BatchStatus, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  exported: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  confirmed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

export default function PaymentBatches() {
  const { userData } = useAuth();
  const [batches, setBatches] = useState<PaymentBatch[] | null>(null);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => onSnapshot(
    query(collection(db, BATCH_COLLECTION)),
    (s) => setBatches(s.docs.map((d) => ({ id: d.id, ...d.data() }) as PaymentBatch)),
    () => setBatches([]),
  ), []);
  useEffect(() => onSnapshot(
    query(collection(db, VOUCHER_COLLECTIONS.vouchers)),
    (s) => setVouchers(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Voucher)),
    () => setVouchers([]),
  ), []);
  useEffect(() => onSnapshot(
    query(collection(db, SUPPLIER_COLLECTION)),
    (s) => setSuppliers(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Supplier)),
    () => setSuppliers([]),
  ), []);

  const actor = { uid: userData?.uid ?? '', name: userData?.displayName };
  const bankFor = (id: string) => suppliers.find((s) => s.id === id)?.bank;

  const available = useMemo(
    () => batchableVouchers(vouchers, batches ?? []),
    [vouchers, batches],
  );

  const open = (batches ?? []).find((b) => b.id === openId);

  if (batches === null) return <div className="p-6 flex justify-center"><Spinner /></div>;

  if (open) {
    return (
      <BatchDetail
        batch={open}
        vouchers={vouchers.filter((v) => open.voucherIds?.includes(v.id))}
        bankFor={bankFor}
        actor={actor}
        onBack={() => setOpenId(null)}
        onError={setErr}
      />
    );
  }

  const sorted = [...batches].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
  const awaiting = batchTotal(available);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={Landmark} tone="navy"
        title="Payment Runs"
        subtitle="Approved vouchers, checked against the bank's rules before the file is written"
      />

      {err && (
        <SoftCard className="mb-4 border-l-4 border-red-400">
          <p className="text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />{err}
          </p>
        </SoftCard>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatChip tone="navy" icon={Landmark} value={money(awaiting)} label="Approved and unpaid" />
        <StatChip tone="slate" value={String(available.length)} label="Vouchers ready to pay" />
        <StatChip tone="slate"
          value={String(sorted.filter((b) => b.status === 'exported').length)}
          label="Runs awaiting confirmation" />
      </div>

      <NewRun
        available={available} bankFor={bankFor} actor={actor}
        onError={setErr} onCreated={setOpenId}
      />

      <SoftCard pad="p-0" className="mt-5">
        <PanelHeader icon={Landmark} title="Runs" tone="navy" subtitle="Newest first" />
        {sorted.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">No payment runs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">Run</th>
                  <th className="px-2 font-semibold">Value date</th>
                  <th className="px-2 font-semibold text-right">Payments</th>
                  <th className="px-2 font-semibold text-right">Total</th>
                  <th className="px-2 pr-4 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((b) => (
                  <tr key={b.id} onClick={() => setOpenId(b.id)}
                    className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer">
                    <td className="py-2.5 pl-4 pr-2 font-mono text-xs text-gray-600 dark:text-gray-300">
                      {b.batchNo}
                    </td>
                    <td className="px-2 tabular-nums text-gray-600 dark:text-gray-300">{b.valueDate}</td>
                    <td className="px-2 text-right tabular-nums">{b.count}</td>
                    <td className="px-2 text-right tabular-nums font-medium">{money(b.total)}</td>
                    <td className="px-2 pr-4">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${STATUS_TONE[b.status]}`}>
                        {BATCH_STATUS_LABEL[b.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SoftCard>
    </div>
  );
}

/* ------------------------------ assembling ------------------------------ */

function NewRun({
  available, bankFor, actor, onError, onCreated,
}: {
  available: Voucher[];
  bankFor: (id: string) => Supplier['bank'];
  actor: { uid: string; name?: string };
  onError: (m: string | null) => void;
  onCreated: (id: string) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [valueDate, setValueDate] = useState(new Date().toISOString().slice(0, 10));
  const [formatId, setFormatId] = useState(BANK_FORMATS[0].id);
  const [busy, setBusy] = useState(false);

  const chosen = available.filter((v) => picked.has(v.id));
  const fmt = bankFormatById(formatId);
  const rows = useMemo(() => batchToBankRows(chosen, bankFor), [chosen, bankFor]);
  const problems = useMemo(() => validateBankRows(rows, fmt), [rows, fmt]);
  const dupes = useMemo(() => duplicateWarnings(chosen, available), [chosen, available]);
  /** voucher id -> its row in the file, so a problem finds its line in one look. */
  const rowIndex = useMemo(
    () => new Map(chosen.map((v, i) => [v.id, i])),
    [chosen],
  );
  const blocked = hasBlockers(problems);

  function toggle(id: string) {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  }

  async function create() {
    setBusy(true); onError(null);
    try {
      const b = await createBatch({ valueDate, bankFormatId: formatId, vouchers: chosen, actor });
      setPicked(new Set());
      onCreated(b.id);
    } catch (e) { onError((e as Error).message); }
    setBusy(false);
  }

  if (available.length === 0) {
    return (
      <SoftCard>
        <p className="text-sm text-gray-500 text-center py-4">
          Nothing is approved and waiting to be paid.
        </p>
      </SoftCard>
    );
  }

  return (
    <SoftCard pad="p-0">
      <PanelHeader
        icon={Landmark} title="Assemble a run" tone="navy"
        subtitle="Only approved vouchers — paying in bulk does not move the approval gate"
        right={(
          <div className="flex items-center gap-2">
            <select value={formatId} onChange={(e) => setFormatId(e.target.value)}
              className={glassFieldClass('max-w-[260px] text-xs')}>
              {BANK_FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
            <input type="date" value={valueDate} onChange={(e) => setValueDate(e.target.value)}
              className={glassFieldClass('max-w-[150px] text-xs')} />
          </div>
        )}
      />

      <div className="overflow-x-auto max-h-[380px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white dark:bg-gray-800">
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
              <th className="py-2 pl-4 pr-2 w-8">
                <input
                  type="checkbox"
                  checked={picked.size === available.length && available.length > 0}
                  onChange={(e) => setPicked(e.target.checked
                    ? new Set(available.map((v) => v.id)) : new Set())}
                />
              </th>
              <th className="px-2 font-semibold">Voucher</th>
              <th className="px-2 font-semibold">Payee</th>
              <th className="px-2 font-semibold">Due</th>
              <th className="px-2 pr-4 font-semibold text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {available.map((v) => {
              const at = rowIndex.get(v.id);
              const rowProblems = at === undefined ? [] : problems.filter((p) => p.index === at);
              const rowDupes = dupes.filter((d) => d.voucherId === v.id);
              return (
                <tr key={v.id} className="border-b border-gray-50 dark:border-gray-800">
                  <td className="py-2 pl-4 pr-2">
                    <input type="checkbox" checked={picked.has(v.id)} onChange={() => toggle(v.id)} />
                  </td>
                  <td className="px-2 font-mono text-xs text-gray-600 dark:text-gray-300">
                    {v.voucherNo}
                  </td>
                  <td className="px-2">
                    <span className="text-gray-800 dark:text-gray-100">{v.payeeName}</span>
                    {rowProblems.map((p) => (
                      <span key={p.field + p.message}
                        className={`block text-[11px] ${
                          p.severity === 'block' ? 'text-red-600' : 'text-amber-600'
                        }`}>
                        {p.severity === 'block' ? '✕' : '!'} {p.message}
                      </span>
                    ))}
                    {rowDupes.map((d) => (
                      <span key={d.againstVoucherNo} className="block text-[11px] text-amber-600">
                        <Copy size={10} className="inline -mt-0.5" /> same amount to this payee on{' '}
                        {d.againstVoucherNo}
                        {d.againstDate === 'this run' ? ' in this run' : ` (${d.daysApart}d ago)`}
                      </span>
                    ))}
                  </td>
                  <td className="px-2 tabular-nums text-gray-500">
                    {v.dueDate?.slice(0, 10) ?? '—'}
                  </td>
                  <td className="px-2 pr-4 text-right tabular-nums">{money(v.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-t border-gray-100 dark:border-gray-700">
        <div className="text-sm">
          <span className="text-gray-500">{picked.size} selected · </span>
          <strong className="text-gray-900 dark:text-white tabular-nums">{money(batchTotal(chosen))}</strong>
          {blocked && (
            <span className="ml-3 text-red-600 text-[13px]">
              <ShieldAlert size={13} className="inline -mt-0.5" /> The bank will reject this file as it stands.
            </span>
          )}
        </div>
        <button type="button" className={btnPrimary}
          disabled={busy || picked.size === 0 || blocked} onClick={create}>
          {busy ? 'Creating…' : 'Create the run'}
        </button>
      </div>
    </SoftCard>
  );
}

/* -------------------------------- detail -------------------------------- */

function BatchDetail({
  batch, vouchers, bankFor, actor, onBack, onError,
}: {
  batch: PaymentBatch;
  vouchers: Voucher[];
  bankFor: (id: string) => Supplier['bank'];
  actor: { uid: string; name?: string };
  onBack: () => void;
  onError: (m: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ConfirmResult | null>(null);

  const fmt = bankFormatById(batch.bankFormatId);
  const rows = useMemo(() => batchToBankRows(vouchers, bankFor), [vouchers, bankFor]);
  const problems = useMemo(() => validateBankRows(rows, fmt), [rows, fmt]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); onError(null);
    try { await fn(); } catch (e) { onError((e as Error).message); }
    setBusy(false);
  }

  function download() {
    const contents = buildBankFile(rows, fmt, { valueDate: batch.valueDate });
    downloadBankFile(contents, `${batch.batchNo.replace(/\//g, '-')}.csv`);
    void run(() => markBatchExported(batch, actor));
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <button type="button" onClick={onBack} className={`${btnGhost} mb-4`}>
        <ArrowLeft size={15} /> All runs
      </button>

      <PageHeader
        icon={Landmark} tone="navy"
        title={batch.batchNo}
        subtitle={`${BATCH_STATUS_LABEL[batch.status]} · value ${batch.valueDate} · ${fmt.label}`}
        actions={(
          <div className="flex gap-2">
            {batch.status === 'draft' && (
              <>
                <button type="button" className={btnPrimary}
                  disabled={busy || hasBlockers(problems)} onClick={download}>
                  <Download size={15} /> Write the file
                </button>
                <button type="button" className={btnGhost} disabled={busy}
                  onClick={() => {
                    const why = window.prompt('Why is this run being cancelled?');
                    if (why) run(() => cancelBatch(batch, why));
                  }}>
                  <Ban size={15} /> Cancel
                </button>
              </>
            )}
            {batch.status === 'exported' && (
              <button type="button" className={btnPrimary} disabled={busy}
                onClick={() => {
                  const ref = window.prompt('The bank reference for this run:') ?? '';
                  run(async () => setResult(await confirmBatch(batch, vouchers, ref, actor)));
                }}>
                <Send size={15} /> Confirm the bank paid it
              </button>
            )}
          </div>
        )}
      />

      {/* Two acts, and the screen says why. */}
      {batch.status === 'exported' && (
        <SoftCard className="mb-4 border-l-4 border-amber-400">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            The file has gone to the bank. Nothing is marked paid until the bank confirms
            it — a bulk file can be rejected whole, and recording the payment now would put
            money in the books on a day it did not leave.
          </p>
        </SoftCard>
      )}

      {result && result.refused.length > 0 && (
        <SoftCard className="mb-4 border-l-4 border-red-400">
          <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">
            {result.paid.length} paid, {result.refused.length} refused by the server.
            The run stays open until they are settled.
          </p>
          <ul className="text-[13px] text-gray-700 dark:text-gray-200 space-y-0.5">
            {result.refused.map((r) => (
              <li key={r.voucherNo}>· <strong>{r.voucherNo}</strong> — {r.reason}</li>
            ))}
          </ul>
        </SoftCard>
      )}

      {hasBlockers(problems) && (
        <SoftCard className="mb-4 border-l-4 border-red-400">
          <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">
            <ShieldAlert size={15} className="inline -mt-0.5 mr-1" />
            The bank would reject this file. Fix these first — the rejection otherwise
            arrives tomorrow, against the whole run.
          </p>
          <ul className="text-[13px] text-gray-700 dark:text-gray-200 space-y-0.5">
            {problems.filter((p) => p.severity === 'block').map((p, i) => (
              <li key={i}>· <strong>{p.payeeName}</strong> — {p.message}</li>
            ))}
          </ul>
        </SoftCard>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatChip tone="navy" value={money(batch.total)} label="Run total" />
        <StatChip tone="slate" value={String(batch.count)} label="Payments" />
        <StatChip tone={batch.status === 'confirmed' ? 'emerald' : 'slate'}
          icon={batch.status === 'confirmed' ? CheckCircle2 : undefined}
          value={batch.bankRef || '—'} label="Bank reference" />
      </div>

      <SoftCard pad="p-0">
        <PanelHeader icon={Landmark} title="What is in the run" tone="navy"
          subtitle="Banking as authorised at approval, not as the register reads now" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                <th className="py-2 pl-4 pr-2 font-semibold">Voucher</th>
                <th className="px-2 font-semibold">Payee</th>
                <th className="px-2 font-semibold">Account</th>
                <th className="px-2 font-semibold">Reference</th>
                <th className="px-2 pr-4 font-semibold text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.map((v, i) => (
                <tr key={v.id} className="border-b border-gray-50 dark:border-gray-800">
                  <td className="py-2 pl-4 pr-2 font-mono text-xs text-gray-600 dark:text-gray-300">
                    {v.voucherNo}
                    {v.currentStatus === 'paid' && (
                      <CheckCircle2 size={12} className="inline ml-1 text-emerald-600" />
                    )}
                  </td>
                  <td className="px-2 text-gray-800 dark:text-gray-100">{v.payeeName}</td>
                  <td className="px-2 font-mono text-xs text-gray-500">
                    {rows[i]?.bank.accountNumber || <span className="text-red-500">missing</span>}
                  </td>
                  <td className="px-2 text-gray-500 text-xs">{rows[i]?.reference}</td>
                  <td className="px-2 pr-4 text-right tabular-nums">{money(v.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SoftCard>
    </div>
  );
}
